import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Field, Input, Textarea } from '../ui/Field';
import { useTerminology } from '../../hooks/useTerminology';
import type { ProfileFieldConfig } from '../../types/profile';
import type { Identity } from '../../types/auth';

/**
 * Editing one registered profile field.
 *
 * The dialog is generated from the field's configuration — its kind decides the
 * control, so a field added to the register becomes editable with no new dialog.
 * Tag fields are edited as real chips rather than a comma-separated string,
 * because "React, ,Python" is the kind of value a text box quietly produces.
 */
export function EditFieldDialog({
  field,
  identity,
  open,
  onClose,
  onSave
}: {
  field: ProfileFieldConfig | null;
  identity: Identity;
  open: boolean;
  onClose: () => void;
  onSave: (patch: Partial<Identity>) => void;
}) {
  const {
    phrase
  } = useTerminology();
  const [text, setText] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!field) return;
    setSaving(false);
    setDraft('');
    if (field.kind === 'tags') {
      setTags(currentTags(identity, field.id));
      setText('');
    } else {
      setText(currentText(identity, field.id));
      setTags([]);
    }
  }, [field, identity, open]);
  if (!field) return null;
  const addTag = () => {
    const value = draft.trim();
    if (!value) return;
    // Case-insensitive, so "Python" and "python" cannot both be stored.
    if (tags.some(tag => tag.toLowerCase() === value.toLowerCase())) {
      setDraft('');
      return;
    }
    setTags(current => [...current, value]);
    setDraft('');
  };
  const save = () => {
    setSaving(true);
    if (field.kind === 'tags') {
      onSave({
        [field.id]: tags
      } as Partial<Identity>);
    } else {
      const trimmed = text.trim();
      onSave({
        [field.id]: trimmed === '' ? null : trimmed
      } as Partial<Identity>);
    }
    onClose();
  };
  return <Modal open={open} onClose={onClose} title={phrase(field.label)} description={phrase(field.purpose)} footer={<>
          <Button variant="ghost" onClick={onClose} data-id="element-4407" data-mp="1tveeo3f13nlo">
            Cancel
          </Button>
          <Button onClick={save} loading={saving} data-id="element-4408" data-mp="28jsioho6li3t">
            Save
          </Button>
        </>} data-id="element-4406" data-mp="1kvribw15p7zh">
      {field.kind === 'longtext' && <Field label={phrase(field.label)} htmlFor="profile-field" data-id="element-4409" data-mp="23kfx0091qh2t">
          <Textarea id="profile-field" rows={4} value={text} placeholder={field.placeholder} onChange={event => setText(event.target.value)} data-id="element-4410" data-mp="2hkz2p0q1hb1t" />
        </Field>}

      {field.kind === 'text' && <Field label={phrase(field.label)} htmlFor="profile-field" data-id="element-4411" data-mp="i7mmp5q7ngjm">
          <Input id="profile-field" value={text} placeholder={field.placeholder} onChange={event => setText(event.target.value)} data-id="element-4412" data-mp="i9fs6hp37uq1" />
        </Field>}

      {field.kind === 'tags' && <div className="space-y-3" data-id="element-4413" data-mp="2l36bo9sw6rnb">
          <Field label={`Add ${phrase(field.label).toLowerCase()}`} hint="Press Enter to add each one." htmlFor="profile-tag" data-id="element-4414" data-mp="2l24xhwgkxp1d">
            <div className="flex gap-2" data-id="element-4415" data-mp="2b3ff82kru2kl">
              <Input id="profile-tag" value={draft} placeholder={field.placeholder} onChange={event => setDraft(event.target.value)} onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addTag();
            }
          }} data-id="element-4416" data-mp="2b6jsdpg7s8ne" />
              <Button variant="secondary" onClick={addTag} disabled={!draft.trim()} data-id="element-4417" data-mp="akvdyh4tz1f">
                Add
              </Button>
            </div>
          </Field>

          {tags.length === 0 ? <p className="text-body-sm text-ink-muted" data-id="element-4418" data-mp="3bqel0dni239t">
              Nothing added yet. This will stay empty on your profile rather than showing a
              placeholder.
            </p> : <ul className="flex flex-wrap gap-2" data-id="element-4419" data-mp="2wvi4ld5je9ln">
              {tags.map(tag => <li key={tag} data-id="element-4420" data-mp="2wxujfhg003uh">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-canvas py-1.5 pl-3 pr-1.5 text-body-sm text-ink" data-id="element-4421" data-mp="2sues90mp6vp6">
                    {tag}
                    <button type="button" onClick={() => setTags(current => current.filter(item => item !== tag))} aria-label={`Remove ${tag}`} className="flex h-6 w-6 items-center justify-center rounded-full text-ink-subtle transition-colors duration-150 ease-out hover:bg-line hover:text-ink" data-id="element-4422" data-mp="2j43d0hxkd02z">
                      <XIcon className="h-3.5 w-3.5" data-id="element-4423" data-mp="kmjka8idbe6a" />
                    </button>
                  </span>
                </li>)}
            </ul>}
        </div>}
    </Modal>;
}
function currentText(identity: Identity, id: ProfileFieldConfig['id']): string {
  if (id === 'bio') return identity.bio ?? '';
  if (id === 'location') return identity.location ?? '';
  return '';
}
function currentTags(identity: Identity, id: ProfileFieldConfig['id']): string[] {
  if (id === 'interests') return identity.interests ?? [];
  if (id === 'skills') return identity.skills ?? [];
  if (id === 'lookingFor') return identity.lookingFor ?? [];
  if (id === 'languages') return identity.languages ?? [];
  return [];
}